<template>
  <div>
    <DistrictPickerBanner />
    
    <a-form-select
      ref="select"
      v-model="selectedClient"
      :label="$t('districtPicker.searchByYourOrganization')"
      :options="options"
      :placeholder="$t('districtPicker.searchByName')"
      option-label="name"
      searchable
      :internal-search="false"
      track-by="name"
      required
      :show-no-options="false"
      open-direction="bottom"
      max-height="500"
      :show-no-results="showNoResults"
      @search-change="searchChange"
    >
      <template #caret="{ toggle }">
        <span class="multiselect__select" @mousedown.prevent.stop="toggle">
          <a-icon icon="16:search" style="font-size: 16px" />
        </span>
      </template>
      <template #option="{ option }">
        <a-text as="p">{{ option.name }}</a-text>
        <a-text as="span" class="address">
          {{ option.city }} {{ option.state }}
        </a-text>
      </template>
      <template #noResult>
        <div class="no-options">
          <a-text as="p">{{ $t('districtPicker.noSearchResults') }}</a-text>
          <a-text as="span" class="search-helper">
            {{ $t('districtPicker.searchHelper') }}
          </a-text>
        </div>
      </template>
    </a-form-select>
    <a-button type="button" class="cred-button" @click="onContinue">
      {{ $t('shared.continue') }}
    </a-button>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import DistrictPickerBanner from './DistrictPickerBanner.vue';
import { useAnalytics } from '../composables/useAnalytics';

import debounce from 'lodash/debounce';
import axios from 'axios';
import { parseURL, joinURL } from 'ufo';

const props = defineProps({
  afterOtpRedirectUri: {
    type: String,
    default: null
  },
  guardianshipSignUp: {
    type: Boolean,
    default: false
  },
  appOauthUid: {
    type: String,
    default: null
  }
});

const MIN_SEARCH_LENGTH = 2;

const selectedClient = ref(null);
const options = ref([]);
const query = ref('');
const hasSearched = ref(false);
//Prevent stale async responses from overwriting newer search state.
const latestRequestId = ref(0);
const $analytics = useAnalytics();
const referrer = document?.referrer;
const start = performance.now();

const showNoResults = computed(() => {
  return query.value.length >= MIN_SEARCH_LENGTH && hasSearched.value;
});

onMounted(() => {
 
  $analytics?.init();
  if (!props.guardianshipSignUp){
    const end = performance.now();
    const duration = end - start;
    $analytics?.track('district_picker_viewed', {
      referrer,
      latency_ms: duration,
      app_oauth_uid: props.appOauthUid,
    });
  }

});

const searchChange = debounce(async (e) => {
  query.value = e;

  if (e.length < MIN_SEARCH_LENGTH) {
    options.value = [];
    hasSearched.value = false;
    return;
  }

  latestRequestId.value += 1;
  const requestId = latestRequestId.value;

  try {
    const startRequest = performance.now();
    const { data } = await axios('/api/v2/districts', {
      params: { search: e }
    });
    const endRequest = performance.now();
    const durationRequest = endRequest - startRequest;

    if (requestId !== latestRequestId.value) {
      return;
    };

    options.value = data.data;
    if (!props.guardianshipSignUp){
      $analytics?.track('district_search_performed', {
        search_term: e,
        referrer,
        results_count: data.data.length,
        latency_ms: durationRequest,
        app_oauth_uid: props.appOauthUid,
      });
    }
  } catch(e){
    
  } finally {
    if (requestId === latestRequestId.value) {
      hasSearched.value = true;
    }
  }
}, 500);

const onContinue = async () => {
  if (!selectedClient.value) {
    return;
  }
  if (!props.guardianshipSignUp){
    $analytics?.track('district_redirect_initiated', {
      district_name: selectedClient.value.name,
      referrer,
      redirect_url: selectedClient.value.redirect_url,
      app_oauth_uid: props.appOauthUid,
    });
  }

  let redirectUrl = selectedClient.value.redirect_url;
  if (props.afterOtpRedirectUri) {
    try {
      // afterOtpRedirectUri is just a path like "/oauth/authorize?client_id=..."
      // Parse the district redirect URL to get the base
      const parsedDistrictUrl = parseURL(selectedClient.value.redirect_url);

      // Get the origin (protocol + host)
      const baseUrl = parsedDistrictUrl.protocol + '//' + parsedDistrictUrl.host;

      // Simply join the base URL with the OIDC path
      // joinURL handles the path and query string correctly
      redirectUrl = joinURL(baseUrl, props.afterOtpRedirectUri);
    } catch (e) {
      // Silently fall back to default redirect_url on error
    }
  }
  if (props.guardianshipSignUp) {
    redirectUrl = joinURL(redirectUrl, 
      'users/sign_up'
    );
  }
  
  window.location.href = redirectUrl;
};

watch(selectedClient, (newVal) => {
  if (newVal && !props.guardianshipSignUp) {
    $analytics?.track('district_selected', {
      district_name: newVal.name,
      city: newVal.city,
      state: newVal.state,
      referrer,
      app_oauth_uid: props.appOauthUid,
    });
  }
});
</script>

<style lang="scss" scoped>
.address {
  color: #747474;
  font-size: 12px;
}

:deep(.form-group__label) {
  text-align: left;
}

:deep(.multiselect__content-wrapper) {
  max-height: 150px !important;
}

:deep(.multiselect--active .multiselect__select) {
  transform: none;
}
:deep(.multiselect__option:has(.no-options)){
  padding: 12px 16px !important;
}

.no-options {
  display: grid;
  gap: 8px;
  text-align: center;
}

.search-helper {
  font-size: 12px;
  color: #747474;
}
</style>
